import { useToken } from '@ori/auth';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import useSWR from 'swr';

import { useConfiguration, useNotificationData } from '../../../contexts';
import { useTranslations } from '../../../hooks/useTranslations_simplified-registration-BrandPartner';
import { MessageType } from '../../../interfaces';
import { TranslationKey } from '../../TranslationKey_simplified-registration-BrandPartner';

const axiosClient = axios.create();

const getEncodedFile = (blob: Blob): string => URL.createObjectURL(blob);

interface FetcherParams {
  url: string;
  tenant: string;
  registrationId: string;
  formattedToken: string;
  previewSize?: { height?: number; width?: number };
}

export function useDocumentThumb(
  customerId: number | null,
  documentId?: number | null,
  previewSize?: { height?: number; width?: number },
) {
  const { globalApiUrl, tenant, registrationId } = useConfiguration();
  const { formattedToken } = useToken();
  const { formatMessage } = useTranslations();

  const {
    context: { notificationData },
  } = useNotificationData();
  const { setNotificationData } = notificationData;

  const errorMeesage = formatMessage(TranslationKey.cannotGetData);

  const thumbUrl = `${globalApiUrl}/customers/${customerId}/documents/${documentId}/image`;
  const shouldFetch = Boolean(customerId && documentId && documentId !== -1);
  const fetchKey = shouldFetch ? `thumb-${customerId}-${documentId}` : null;

  const fetcher = async ({
    url,
    tenant,
    registrationId,
    formattedToken,
    previewSize,
  }: FetcherParams): Promise<string> => {
    try {
      const headers: AxiosRequestConfig['headers'] = {
        'x-tenant-context': tenant,
        'x-registration-id': registrationId,
        Authorization: formattedToken,
      };
      const response = await axiosClient.get<Blob>(url, {
        headers,
        responseType: 'blob',
        params: previewSize,
      });
      return getEncodedFile(response.data);
    } catch (error) {
      setNotificationData({
        isOpen: true,
        message: errorMeesage,
        type: MessageType.error,
        messageId: 'cannotGetData',
      });
      const axiosError = error as AxiosError;
      throw new Error(axiosError.message);
    }
  };

  const { data: documentURL } = useSWR<string, AxiosError>(
    fetchKey ? [fetchKey, { url: thumbUrl, tenant, registrationId, formattedToken, previewSize }] : null,
    () => fetcher({ url: thumbUrl, tenant, registrationId, formattedToken, previewSize }),
  );

  return documentURL;
}
